import { useEffect, useState } from "react";
import { apiGet } from "./api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3 } from "lucide-react";
import AdminPageLayout from "./AdminPageLayout";

interface AutomationLog {
  id: number;
  job: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
  metrics_json: string | null;
}

export default function Dashboard() {
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log("[Dashboard] Loading automation logs...");
    apiGet<{ logs: AutomationLog[] }>("/api/admin/logs?limit=20")
      .then((data) => {
        console.log("[Dashboard] ✅ Logs loaded:", data.logs?.length || 0);
        setLogs(data.logs || []);
      })
      .catch((err) => {
        console.error("[Dashboard] ❌ Error loading logs:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminPageLayout
      title="Dashboard"
      description="Recent automation jobs and system status"
      icon={<BarChart3 className="w-8 h-8" />}
      loading={loading}
      error={error}
    >
      <Card>
        <CardHeader>
          <CardTitle>Automation Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-automation-logs">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium">ID</th>
                  <th className="text-left p-2 font-medium">Job</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-left p-2 font-medium">Started</th>
                  <th className="text-left p-2 font-medium">Finished</th>
                  <th className="text-left p-2 font-medium">Metrics</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center p-8 text-muted-foreground">
                      No automation logs yet
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b hover-elevate" data-testid={`log-${log.id}`}>
                      <td className="p-2">{log.id}</td>
                      <td className="p-2">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {log.job}
                        </code>
                      </td>
                      <td className="p-2">
                        <Badge
                          variant={log.status === "success" ? "default" : "destructive"}
                          data-testid={`status-${log.id}`}
                        >
                          {log.status}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {new Date(log.started_at).toLocaleString()}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {log.finished_at
                          ? new Date(log.finished_at).toLocaleString()
                          : "-"}
                      </td>
                      <td className="p-2">
                        {log.metrics_json ? (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-primary">
                              View
                            </summary>
                            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                              {JSON.stringify(JSON.parse(log.metrics_json), null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
